/**
 * The editor for a `table` field (SA-449, spec §8): one row per item, one
 * column per declared column, each cell the scalar control. The core checks
 * the shape on complete — a 422 names the row and the column — so this
 * pre-checks nothing. An emptied table is `null`, so blank means one thing.
 * `Cell` is `FieldInput` itself, handed in by it: no import cycle.
 */
import type { ComponentType } from 'react'
import { Button } from '@/components/ui'
import { cn } from '@/utils'
import type { FieldLike } from './FieldInput'
import { NEW_ROW, changedFrom, wasText } from './fieldStateText'
import { cellChange, formatCell, rowAdded, rowsOf, visibleColumns, type TableRow } from './tableRows'

type CellProps = { field: FieldLike; value: unknown; onChange: (v: unknown) => void; idPrefix?: string; describedBy?: string }

export function TableFieldInput({ field, value, onChange, idPrefix = 'f', describedBy, Cell }: {
  field: FieldLike
  value: unknown
  onChange: (v: unknown) => void
  idPrefix?: string
  /** SA-1196: the field's status line, which describes the whole group. */
  describedBy?: string
  Cell: ComponentType<CellProps>
}) {
  const declared = field.columns ?? []
  const columns = visibleColumns(declared)
  const labelOf = (cid: string) => declared.find((c) => c.id === cid)?.label ?? cid
  const rows = rowsOf(value)
  const id = `${idPrefix}-${field.id}`
  const blank = (): TableRow => Object.fromEntries(declared.map((c) => [c.id, null]))
  const emit = (next: TableRow[]) => onChange(next.length ? next : null)
  const setCell = (at: number, column: string, v: unknown) => emit(rows.map((row, i) => (i === at ? { ...row, [column]: v } : row)))
  const insertAt = (at: number) => emit([...rows.slice(0, at), blank(), ...rows.slice(at)])
  const removeAt = (at: number) => emit(rows.filter((_, i) => i !== at))
  return (
    <div id={id} role="group" aria-label={field.label ?? field.id} aria-describedby={describedBy} className="space-y-2">
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                {columns.map((c) => <th key={c.id} scope="col" className="px-1 pb-1 font-medium">{c.label}</th>)}
                <th scope="col"><span className="sr-only">Row actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const added = rowAdded(row, declared)
                return (
                  <tr key={i} className="align-top" data-row={added ? 'added' : undefined}>
                    {columns.map((c) => {
                      const change = cellChange(row, c, declared)
                      const cellId = `${id}-${i}-${c.id}`
                      const reference = c.reference ? row[c.reference] : undefined
                      const was = change === 'changed' ? formatCell(reference, c.type) : null
                      return (
                        <td key={c.id} data-change={change === 'changed' ? change : undefined}
                          className={cn('px-1 py-1', change === 'changed' && 'border-l-2 border-l-role-person pl-1.5')}>
                          <label htmlFor={cellId} className="sr-only">{`${c.label}, row ${i + 1}`}</label>
                          <Cell field={c} value={row[c.id] ?? null} onChange={(v) => setCell(i, c.id, v)} idPrefix={`${id}-${i}`}
                            describedBy={was !== null ? `${cellId}-was` : undefined} />
                          {was !== null && (
                            <p id={`${cellId}-was`} className="text-xs text-muted-foreground">
                              <span className="sr-only">{changedFrom(`${was} (${labelOf(c.reference as string)})`)}</span>
                              <s aria-hidden="true">{wasText(reference, c.type)}</s>
                            </p>
                          )}
                        </td>
                      )
                    })}
                    <td className="whitespace-nowrap px-1 py-1">
                      {added && <span className="mr-2 text-xs text-muted-foreground">{NEW_ROW}</span>}
                      <Button type="button" size="sm" variant="ghost" aria-label={`Insert below row ${i + 1}`} onClick={() => insertAt(i + 1)}>
                        Insert below
                      </Button>
                      <Button type="button" size="sm" variant="ghost" aria-label={`Remove row ${i + 1}`} onClick={() => removeAt(i)}>
                        Remove
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <Button type="button" size="sm" variant="outline" onClick={() => insertAt(rows.length)}>Add row</Button>
    </div>
  )
}
